// A Take Off line's supplier quote (#654): Upload file saves the PDF to the job site's Quotes folder,
// then the line keeps its link and opens it from SharePoint (Edge from the iPad web clip). Taking it
// off the line leaves the file on the site.
import { useRef, useState } from 'react';
import { OutboundLink } from '@higgins/ui';
import { Paperclip, Upload, X } from 'lucide-react';
import type { SupplierQuote } from '@higgins/m2q-pricing';
import { ApiError, isConnectionFailure, uploadSupplierQuoteApi } from '../lib/api';
import { useOnline } from '../lib/hooks';
import { RemoveButton } from './remove-button';

const PDF_TYPES = 'application/pdf,.pdf';

function describe(err: unknown): string {
  if (isConnectionFailure(err)) return 'Uploading the quote needs signal.';
  if (err instanceof ApiError) return err.message;
  return 'The quote could not be uploaded.';
}

interface Props {
  optionId: string;
  quote: SupplierQuote | undefined;
  /** What the line is, for the questions and labels ("Engineer"). */
  lineName: string;
  /** Record the quote on the line, or take it off (null). */
  onQuote: (quote: SupplierQuote | null) => void | Promise<void>;
}

export function SupplierQuoteControl({ optionId, quote, lineName, onQuote }: Props) {
  const online = useOnline();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File) {
    setBusy(true);
    setError(null);
    try {
      await onQuote(await uploadSupplierQuoteApi(optionId, file.name, file));
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  if (quote) {
    return (
      <span className="flex items-center gap-1 text-xs">
        <OutboundLink openIn="edge" href={quote.webUrl} className="flex max-w-40 items-center gap-1 text-primary" title={`${quote.name}, in the job's Quotes folder on SharePoint`}>
          <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden /> <span className="truncate">{quote.name}</span>
        </OutboundLink>
        <RemoveButton
          question={`Take the quote ${quote.name} off ${lineName || 'this line'}? The file stays in the job's Quotes folder on SharePoint.`}
          onRemove={() => void onQuote(null)}
          label={`Take the quote off ${lineName || 'this line'}`}
          title="Take the quote off this line"
          className="text-slate-400 hover:text-red-700"
        >
          <X className="h-3.5 w-3.5" />
        </RemoveButton>
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-xs">
      <button
        type="button"
        disabled={busy || !online}
        onClick={() => input.current?.click()}
        title={online ? 'Upload the supplier’s quote (PDF) to the job’s Quotes folder' : 'Uploading a quote needs signal.'}
        className="flex items-center gap-1 rounded border border-slate-300 px-1.5 py-0.5 text-slate-600 disabled:opacity-50"
      >
        <Upload className="h-3.5 w-3.5" aria-hidden /> {busy ? 'Uploading…' : 'Upload file'}
      </button>
      <input
        ref={input}
        type="file"
        accept={PDF_TYPES}
        className="hidden"
        aria-label={`Supplier quote for ${lineName || 'this line'} (PDF)`}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void upload(file);
        }}
      />
      {error && <span className="text-red-700">{error}</span>}
    </span>
  );
}
